import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";

const page = (loader) => lazy(loader);
const Schools = page(() => import("@/pages/Schools"));
const Parties = page(() => import("@/pages/Parties"));
const Books = page(() => import("@/pages/Books"));
const Publishers = page(() => import("@/pages/Publishers"));
const Booklists = page(() => import("@/pages/Booklists"));
const StationeryLists = page(() => import("@/pages/StationeryLists"));
const PurchaseOrders = page(() => import("@/pages/PurchaseOrders"));
const Receipts = page(() => import("@/pages/Receipts"));
const Claims = page(() => import("@/pages/Claims"));
const BookDemand = page(() => import("@/pages/BookDemand"));
const Bundling = page(() => import("@/pages/Bundling"));
const Stock = page(() => import("@/pages/Stock"));
const StockAdjustment = page(() => import("@/pages/StockAdjustment"));
const Samples = page(() => import("@/pages/Samples"));
const SalesOrders = page(() => import("@/pages/SalesOrders"));
const Shipments = page(() => import("@/pages/Shipments"));
const Batches = page(() => import("@/pages/Batches"));
const Scan = page(() => import("@/pages/Scan"));
const MyWork = page(() => import("@/pages/MyWork"));
const DataMasters = page(() => import("@/pages/DataMasters"));
const SetPacking = page(() => import("@/pages/SetPacking"));
const Settings = page(() => import("@/pages/Settings"));
const ResetPassword = page(() => import("@/pages/ResetPassword"));
const BooklistGrid = page(() => import("@/pages/BooklistGrid"));
const Logs = page(() => import("@/pages/Logs"));
const BatchDetail = page(() => import("@/pages/BatchDetail"));
const PublisherReturns = page(() => import("@/pages/PublisherReturns"));
const PendingBillingPage = page(() => import("@/pages/PendingBillingPage"));
const Manual = page(() => import("@/pages/Manual"));
const Reallocation = page(() => import("@/pages/Reallocation"));
const PoBillShip = page(() => import("@/pages/PoBillShip"));
const CoverLetters = page(() => import("@/pages/CoverLetters"));
const DataAudit = page(() => import("@/pages/DataAudit"));
const PriceHistory = page(() => import("@/pages/PriceHistory"));

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center label-xs" data-testid="page-loading">Loading…</div>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center label-xs" data-testid="auth-loading">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  const routes = [
    ["/", <Dashboard />],
    ["/my-work", <MyWork />],
    ["/pending-report", <BookDemand />],
    ["/schools", <Schools />],
    ["/parties", <Parties />],
    ["/books", <Books />],
    ["/publishers", <Publishers />],
    ["/booklists", <Booklists />],
    ["/stationery-lists", <StationeryLists />],
    ["/booklists/grid", <BooklistGrid />],
    ["/logs", <Logs />],
    ["/purchase-orders", <PurchaseOrders />],
    ["/receipts", <Receipts />],
    ["/claims", <Claims />],
    ["/bundling", <Bundling />],
    ["/packing", <SetPacking />],
    ["/stock", <Stock />],
    ["/stock-adjustment", <StockAdjustment />],
    ["/samples", <Samples />],
    ["/sales-orders", <SalesOrders />],
    ["/shipments", <Shipments />],
    ["/batches", <Batches />],
    ["/cover-letters", <CoverLetters />],
    ["/batches/:id", <BatchDetail />],
    ["/publisher-returns", <PublisherReturns />],
    ["/reallocation", <Reallocation />],
    ["/pending-billing", <PendingBillingPage />],
    ["/manual", <Manual />],
    ["/po-billship", <PoBillShip />],
    ["/scan", <Scan />],
    ["/scan/:shipmentNo", <Scan />],
    ["/data", <DataMasters />],
    ["/settings", <Settings />],
    ["/data-audit", <DataAudit />],
    ["/price-history", <PriceHistory />],
  ];
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {routes.map(([path, el]) => (
                <Route key={path} path={path} element={<Protected>{el}</Protected>} />
              ))}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
